import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardMedia,
} from '@mui/material';
import Logo from '../../components/common/Logo';
import { offerDetails } from '../../services/api';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

interface OfferData {
  title: string;
  image: string;
  price: string;
  id?: number;
}

interface Tokens {
  accessToken?: string;
  refreshToken?: string;
}

const CheckoutForm: React.FC<{ offerData: OfferData; offerCode: string; tokens?: Tokens; email?: string }> = ({ offerData, offerCode, tokens, email }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Create payment intent
      const amount = parseFloat(offerData.price) * 100; // Convert to cents
      if (!offerData.id) {
        setError('Offer ID is missing');
        setLoading(false);
        return;
      }
      const paymentIntentResponse = await offerDetails.createPaymentIntent(
        amount,
        'usd',
        'Paid_For_Item',
        offerData.id,
        tokens?.accessToken
      );

      if (!paymentIntentResponse.success) {
        setError(paymentIntentResponse.message || 'Failed to create payment intent');
        setLoading(false);
        return;
      }

      const clientSecret = paymentIntentResponse.data.clientSecret;

      // Confirm payment with Stripe
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement)!,
          },
        }
      );

      if (stripeError) {
        setError(stripeError.message || 'Payment failed');
        // If payment failed in Stripe, we still want to record the failed attempt if needed
        try {
          await offerDetails.confirmBuyerPayment(
            'failed',
            offerData.id!,
            '',
            clientSecret,
            tokens?.accessToken
          );
        } catch (confirmError) {
          console.error('Error confirming failed payment:', confirmError);
        }
      } else if (paymentIntent.status === 'succeeded') {
        setSuccess(true);
        // Call confirmBuyerPayment with succeeded status
        try {
          const confirmResponse = await offerDetails.confirmBuyerPayment(
            'succeeded',
            offerData.id!,
            paymentIntent.id,
            clientSecret,
            tokens?.accessToken
          );
          
          // Navigate to OfferCompletion page with tracking ID
          setTimeout(() => {
            navigate(`/offer/${offerCode}/completion`, {
              state: {
                paymentSuccess: true,
                trackingId: confirmResponse.data?.trackingId || '',
                email: email || '',
                offerData
              }
            });
          }, 2000);
        } catch (confirmError) {
          console.error('Error confirming payment:', confirmError);
          setError('Payment succeeded but confirmation failed. Please contact support.');
        }
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.message || err?.message || 'Payment failed';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Card Details
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <CardElement 
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#424770',
                  '::placeholder': {
                    color: '#aab7c4',
                  },
                },
              },
              disableLink: true,
            }}
          />
        </Paper>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          Payment successful! Redirecting...
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={!stripe || loading || success}
        sx={{
          borderRadius: 2,
          py: 1.5,
          textTransform: 'none',
          fontWeight: 'bold',
          mb: 2,
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : `Pay $${offerData.price}`}
      </Button>
    </form>
  );
};

const Payment: React.FC = () => {
  const { offerCode } = useParams<{ offerCode: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [offerData, setOfferData] = useState<OfferData | null>(null);
  const [offerLoading, setOfferLoading] = useState(true);
  const [tokens, setTokens] = useState<Tokens | undefined>(undefined);
  const [email, setEmail] = useState<string>('');

  useEffect(() => {
    const fetchOfferDetails = async () => {
      if (!offerCode) {
        return;
      }

      // Check if offerData is passed from previous page
      if (location.state?.offerData) {
        setOfferData({
          title: location.state.offerData.title,
          image: location.state.offerData.image,
          price: location.state.offerData.price,
          id: location.state.offerData.id,
        });
        // Get tokens from location state
        if (location.state?.tokens) {
          setTokens(location.state.tokens);
        }
        if (location.state?.email) {
          setEmail(location.state.email);
        }
        setOfferLoading(false);
        return;
      }

      try {
        const response = await offerDetails.getOfferByIdForGuestUser(offerCode);
        if (response.success) {
          setOfferData({
            title: response.data.title,
            image: response.data.image,
            price: response.data.price,
            id: response.data.id,
          });
        }
      } catch (err) {
        console.error('Error fetching offer details:', err);
      } finally {
        setOfferLoading(false);
      }
    };

    fetchOfferDetails();
  }, [offerCode, location.state]);

  const handleBack = () => {
    navigate(`/offer/${offerCode}/buyer-details`);
  };

  return (
    <Container maxWidth="sm" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
        <Logo variant="full" size="small" />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 4,
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        {offerLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {offerData && (
              <Box sx={{ mb: 4, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                  {offerData.image && (
                    <Card sx={{ width: 100, height: 100, flexShrink: 0 }}>
                      <CardMedia
                        component="img"
                        sx={{ height: 100, objectFit: 'cover' }}
                        image={offerData.image}
                        alt={offerData.title}
                      />
                    </Card>
                  )}
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight="bold" gutterBottom>
                      {offerData.title}
                    </Typography>
                    <Typography variant="h5" color="primary" fontWeight="bold">
                      ${offerData.price}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
              Payment
            </Typography>

            <Alert severity="info" sx={{ mb: 3 }}>
              Complete your payment to finalize the purchase.
            </Alert>

            {stripePromise && offerData && (
              <Elements stripe={stripePromise}>
                <CheckoutForm offerData={offerData} offerCode={offerCode || ''} tokens={tokens} email={email} />
              </Elements>
            )}

            <Alert severity="info" sx={{ mb: 3, mt: 2 }}>
              Your amount will be held in escrow until you receive the item and are satisfied with it.
            </Alert>

            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={handleBack}
              disabled={offerLoading}
              sx={{
                borderRadius: 2,
                py: 1.5,
                textTransform: 'none',
                fontWeight: 'bold',
              }}
            >
              Back
            </Button>

            {/* Security indicator */}
            <Box sx={{ mt: 4, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <svg width="40" height="25" viewBox="0 0 40 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.5 17.5H16.5V7.5H14.5V17.5ZM11.5 17.5H13.5V7.5H11.5V17.5ZM8.5 17.5H10.5V7.5H8.5V17.5Z" fill="#635BFF"/>
                  <path d="M25 17.5H27V7.5H25V17.5ZM22 17.5H24V7.5H22V17.5ZM19 17.5H21V7.5H19V17.5Z" fill="#635BFF"/>
                  <path d="M35.5 17.5H37.5V7.5H35.5V17.5ZM32.5 17.5H34.5V7.5H32.5V17.5ZM29.5 17.5H31.5V7.5H29.5V17.5Z" fill="#635BFF"/>
                </svg>
                Secured by Stripe
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Your payment information is encrypted and secure
              </Typography>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default Payment;
